import { atomWithReset as stateSegmentWithReset } from 'jotai/utils';

// File upload stage enum
export type FileUploadStage =
  | 'uploading' // File upload in progress (0-100%)
  | 'extracting' // Metadata extraction in progress (0-100%)
  | 'analyzing' // Analysis in progress (no percentage)
  | 'completed' // All stages complete
  | 'error'; // Error at any stage

// Error structure with stage information
export type FileError = {
  stage: 'uploading' | 'extracting' | 'analyzing';
  message: string;
  title?: string;
};

// Queue entry for files waiting to be uploaded
export type QueuedFile = {
  file: File;
  queueId: string; // Temporary ID just for queue tracking (not a fileId)
};

// Per-file state
export type FileState = {
  fileId: string;
  fileName: string;
  fileSize?: number | string; // number for File.size during upload, string from API (e.g., "23.01 MB")

  // NEW: Single status field instead of multiple booleans
  status: FileUploadStage;

  // NEW: Progress tracking (0-100)
  uploadProgress: number; // For uploading stage
  extractionProgress: number; // For extracting stage

  // NEW: Structured error tracking
  error?: FileError;

  // Retry tracking
  retryCount: number;

  // Deletion state
  isDeleting: boolean;

  // Repository data (from /v1/file/repository API)
  description?: string;
  customFileName?: string;
  thumbnail?: string;
  extractedMetadata?: {
    brand?: string;
    country?: string;
  };
};

// Session-level state
export type FileUploads = {
  // Session-level fields
  sessionId: string;

  // NEW: Session initialization tracking
  isInitializingSession: boolean;

  // NEW: Queue for files waiting for session ID
  uploadQueue: QueuedFile[];

  // Session restore fields
  restore: boolean;
  contextSessionId: string;
  sessionContextRestored: boolean;
  sessionDetailsFetched: boolean;

  // Repository fetch state
  isLoadingRepository: boolean;
  repositoryFetchError: boolean;
  repositoryRetryCount: number;

  // Multi-file data
  files: Map<string, FileState>;

  // File selection for marketing panel
  selectedCompletedFileIds: string[];
};

export const fileUploadSegment = stateSegmentWithReset<FileUploads>({
  // Session-level fields
  sessionId: '',

  // Session initialization
  isInitializingSession: false,

  // Upload queue
  uploadQueue: [],

  // Session restore fields
  restore: false,
  contextSessionId: '',
  sessionContextRestored: false,
  sessionDetailsFetched: false,

  // Repository fetch state
  isLoadingRepository: false,
  repositoryFetchError: false,
  repositoryRetryCount: 0,

  // Multi-file data
  files: new Map<string, FileState>(),

  // File selection for marketing panel
  selectedCompletedFileIds: [],
});

fileUploadSegment.debugLabel = 'fileUploadSegment';
